import type { GridGeometry } from '../../grid';
import type { MapDefinition, TilePosition } from '../../model/gamestate/GameState';
import type { PixelPosition } from './types';
import type { WorldRect } from './board';

export class Viewport {
    private cameraOffset = { x: 0, y: 0 };
    private containerRect: DOMRect | null = null;
    private tileSize: number;

    constructor(tileSize: number) {
        this.tileSize = tileSize;
    }

    public setContainerRect(rect: DOMRect): void {
        this.containerRect = rect;
    }

    public setTileSize(tileSize: number): void {
        this.tileSize = tileSize;
    }

    public setCameraOffset(x: number, y: number): void {
        this.cameraOffset = { x, y };
    }

    public getCameraOffset(): { x: number; y: number } {
        return this.cameraOffset;
    }

    public updateCameraFromWorldCenter(center: PixelPosition, container: HTMLElement): void {
        this.containerRect = container.getBoundingClientRect();
        const viewportWidth = this.containerRect.width;
        const viewportHeight = this.containerRect.height;

        // Calculate offset to center player in viewport
        const translateX = viewportWidth / 2 - center.x;
        const translateY = viewportHeight / 2 - center.y;

        this.cameraOffset = { x: translateX, y: translateY };
    }

    public worldToScreen(x: number, y: number, grid: GridGeometry): { x: number; y: number } {
        const p = grid.tileToPixel(x, y, this.tileSize);
        return {
            x: p.x + this.cameraOffset.x,
            y: p.y + this.cameraOffset.y
        };
    }

    public worldToScreenCenter(x: number, y: number, grid: GridGeometry): { x: number; y: number } {
        const screen = this.worldToScreen(x, y, grid);
        const c = this.getInterpolatedTileCentroid(x, y, grid);
        return { x: screen.x + c.x, y: screen.y + c.y };
    }

    public worldCenterToScreen(center: PixelPosition): { x: number; y: number } {
        return {
            x: center.x + this.cameraOffset.x,
            y: center.y + this.cameraOffset.y
        };
    }

    public worldTileCenter(x: number, y: number, grid: GridGeometry): { x: number; y: number } {
        const world = grid.tileToPixel(x, y, this.tileSize);
        const centroid = this.getInterpolatedTileCentroid(x, y, grid);
        return { x: world.x + centroid.x, y: world.y + centroid.y };
    }

    public screenToTile(
        canvasX: number,
        canvasY: number,
        grid: GridGeometry,
        mapWidth: number,
        mapHeight: number,
        margin = 0,
    ): TilePosition | null {
        // Convert screen coordinates into world tile coordinates using camera offset
        const worldX = canvasX - this.cameraOffset.x;
        const worldY = canvasY - this.cameraOffset.y;
        const tile = grid.pixelToTile(worldX, worldY, this.tileSize);

        if (tile.q < -margin || tile.r < -margin || tile.q >= mapWidth + margin || tile.r >= mapHeight + margin) {
            return null;
        }

        return { x: tile.q, y: tile.r };
    }

    public screenToWorld(canvasX: number, canvasY: number): { x: number; y: number } {
        return {
            x: canvasX - this.cameraOffset.x,
            y: canvasY - this.cameraOffset.y
        };
    }

    public getVisibleWorldRect(margin = 0): WorldRect {
        const width = this.containerRect?.width ?? 0;
        const height = this.containerRect?.height ?? 0;
        const left = -this.cameraOffset.x - margin;
        const top = -this.cameraOffset.y - margin;
        const right = left + width + margin * 2;
        const bottom = top + height + margin * 2;
        return {
            left,
            top,
            right,
            bottom,
            width: Math.max(0, right - left),
            height: Math.max(0, bottom - top),
        };
    }

    /**
     * Interpolate tile centroid continuously for fractional tile coordinates.
     * This prevents visible jitter on grids (notably triangles) where centroid
     * differs by tile parity while entities are moving between tiles.
     */
    public getInterpolatedTileCentroid(x: number, y: number, grid: GridGeometry): { x: number; y: number } {
        const q0 = Math.floor(x);
        const r0 = Math.floor(y);
        const fx = x - q0;
        const fy = y - r0;

        const c00 = grid.tileCentroid(this.tileSize, q0, r0);
        if (fx === 0 && fy === 0) {
            return c00;
        }

        const c10 = grid.tileCentroid(this.tileSize, q0 + 1, r0);
        const c01 = grid.tileCentroid(this.tileSize, q0, r0 + 1);
        const c11 = grid.tileCentroid(this.tileSize, q0 + 1, r0 + 1);

        const cx =
            c00.x * (1 - fx) * (1 - fy) +
            c10.x * fx * (1 - fy) +
            c01.x * (1 - fx) * fy +
            c11.x * fx * fy;
        const cy =
            c00.y * (1 - fx) * (1 - fy) +
            c10.y * fx * (1 - fy) +
            c01.y * (1 - fx) * fy +
            c11.y * fx * fy;

        return { x: cx, y: cy };
    }
}
